import { createAction, props } from '@ngrx/store';

import {
  GetLoginSettingsResponse,
  UpdateLoginSettingsPayload,
  UpdateLoginSettingsResponse,
  IpWhitelistListResponse,
  IpWhitelistDetailResponse,
  CreateIpRestrictionPayload,
  CreateIpRestrictionResponse,
  UpdateIpRestrictionStatusPayload,
  UpdateIpRestrictionStatusResponse,
  UserListResponse,
} from '../models/user-access-management.model';

// Login Settings Actions
export const getLoginSettings = createAction(
  '[User Access Management] Get Login Settings'
);

export const getLoginSettingsSuccess = createAction(
  '[User Access Management] Get Login Settings Success',
  props<{ response: GetLoginSettingsResponse }>()
);

export const getLoginSettingsFailure = createAction(
  '[User Access Management] Get Login Settings Failure',
  props<{ error: string }>()
);

export const updateLoginSettings = createAction(
  '[User Access Management] Update Login Settings',
  props<{ payload: UpdateLoginSettingsPayload }>()
);

export const updateLoginSettingsSuccess = createAction(
  '[User Access Management] Update Login Settings Success',
  props<{ response: UpdateLoginSettingsResponse }>()
);

export const updateLoginSettingsFailure = createAction(
  '[User Access Management] Update Login Settings Failure',
  props<{ error: string }>()
);

// IP Whitelist Actions
export const loadIpWhitelistList = createAction(
  '[IP Whitelist] Load IP Whitelist List',
  props<{ page: number; limit: number }>()
);

export const loadIpWhitelistListSuccess = createAction(
  '[IP Whitelist] Load IP Whitelist List Success',
  props<{ response: IpWhitelistListResponse }>()
);

export const loadIpWhitelistListFailure = createAction(
  '[IP Whitelist] Load IP Whitelist List Failure',
  props<{ error: string }>()
);

export const loadIpWhitelistDetail = createAction(
  '[IP Whitelist] Load IP Whitelist Detail',
  props<{ userId: string }>()
);

export const loadIpWhitelistDetailSuccess = createAction(
  '[IP Whitelist] Load IP Whitelist Detail Success',
  props<{ response: IpWhitelistDetailResponse }>()
);

export const loadIpWhitelistDetailFailure = createAction(
  '[IP Whitelist] Load IP Whitelist Detail Failure',
  props<{ error: string }>()
);

export const setIpWhitelistEnabled = createAction(
  '[IP Whitelist] Set IP Whitelist Enabled',
  props<{ enabled: boolean }>()
);

export const clearIpWhitelistDetail = createAction(
  '[IP Whitelist] Clear IP Whitelist Detail'
);

export const setIpWhitelistPage = createAction(
  '[IP Whitelist] Set IP Whitelist Page',
  props<{ pageIndex: number; pageSize: number }>()
);

export const createIpRestriction = createAction(
  '[IP Whitelist] Create IP Restriction',
  // `'system'` writes the platform-wide whitelist; anything else is a user id.
  // Never null: a missing user must not mean "system" (F-183).
  props<{ userId: string; payload: CreateIpRestrictionPayload }>()
);

export const createIpRestrictionSuccess = createAction(
  '[IP Whitelist] Create IP Restriction Success',
  props<{ response: CreateIpRestrictionResponse }>()
);

export const createIpRestrictionFailure = createAction(
  '[IP Whitelist] Create IP Restriction Failure',
  props<{ error: string }>()
);

export const deleteIpRestriction = createAction(
  '[IP Whitelist] Delete IP Restriction',
  props<{ userId: string }>()
);

export const deleteIpRestrictionSuccess = createAction(
  '[IP Whitelist] Delete IP Restriction Success'
);

export const deleteIpRestrictionFailure = createAction(
  '[IP Whitelist] Delete IP Restriction Failure',
  props<{ error: string }>()
);

export const updateIpRestrictionStatus = createAction(
  '[IP Whitelist] Update IP Restriction Status',
  props<{ userId: string; payload: UpdateIpRestrictionStatusPayload }>()
);

export const updateIpRestrictionStatusSuccess = createAction(
  '[IP Whitelist] Update IP Restriction Status Success',
  props<{ response: UpdateIpRestrictionStatusResponse }>()
);

export const updateIpRestrictionStatusFailure = createAction(
  '[IP Whitelist] Update IP Restriction Status Failure',
  props<{ error: string }>()
);

// User List Actions (GET /api/users — see UserAccessManagementService.getUserList).
// Prefixed with this feature's own name: the users page declares
// '[User List] Load User List' too, and while the two shared a type the users
// page's effect answered this feature's dispatches with its own paging and
// filters (F-182). `status` omitted = every status.
export const loadUserList = createAction(
  '[IP Whitelist] Load User List',
  props<{
    status?: string;
    page?: number;
    limit?: number;
  }>()
);

export const loadUserListSuccess = createAction(
  '[IP Whitelist] Load User List Success',
  props<{ response: UserListResponse }>()
);

export const loadUserListFailure = createAction(
  '[IP Whitelist] Load User List Failure',
  props<{ error: string }>()
);
